import { isPlatformBrowser } from '@angular/common';
import type { ElementRef, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Component, inject, input, linkedSignal, PLATFORM_ID, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { Subscription } from 'rxjs';

import type { TInputComponentSize } from 'components/atoms/input-v2/entities/types';
import { InputV2Component } from 'components/atoms/input-v2/input-v2.component';
import { LocaleService } from 'src/services/locale/locale.service';
import { SearchService } from 'src/services/search/search.service';
import { UrlService } from 'src/services/url/url.service';

@Component({
    selector: 'website-search-input',
    templateUrl: './search-input.component.html',
    imports: [FormsModule, InputV2Component],
})
export class SearchInputComponent implements OnInit, OnDestroy, OnChanges {
    private readonly platformId = inject(PLATFORM_ID);
    private searchService = inject(SearchService);
    private readonly urlService = inject(UrlService);
    readonly localeService = inject(LocaleService);

    public readonly actionEnter = input(false);
    public readonly inputSize = input<TInputComponentSize>('large');
    public readonly searchInput = input('');
    public readonly inputPlaceholder = input<string>();

    public searchInputValue = linkedSignal(() => this.searchInput());

    public readonly inputElement = viewChild.required<ElementRef>('inputElement');

    private resetSearchSubscription: Subscription;
    private firstUrlSegment: string;

    ngOnInit() {
        this.firstUrlSegment = this.urlService.getFirstUrlSegment();
        this.resetSearchSubscription = this.searchService.resetSearchEvent$.subscribe(() => {
            this.searchInputValue.set('');
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isOpened && changes.isOpened.currentValue) {
            this.inputElement().nativeElement.focus();
        }
    }

    ngOnDestroy(): void {
        this.resetSearchSubscription.unsubscribe();
    }

    public performSearch() {
        const trimmedSearchInput = this.searchInputValue() ? this.searchInputValue().trim() : '';

        if (isPlatformBrowser(this.platformId) && this.actionEnter()) {
            // Navigate to search page for non-search pages
            window.location.href =
                this.localeService.getPathToLocaleSlug() +
                '/' +
                this.firstUrlSegment +
                '/search/?q=' +
                trimmedSearchInput;
        } else {
            // Emit search event for search pages (when actionEnter is false)
            this.searchService.emitSearchEvent(trimmedSearchInput);
        }
    }
}
